import { LoadingButton } from '@mui/lab';
import { Box, Button, DialogTitle, Grid } from '@mui/material';
import { driver } from 'driver.js';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useParams } from 'react-router';
import { DialogAnimate } from 'src/components/animate';
import Avatar from 'src/components/Avatar';
import { GeneralDatePicker, GeneralSelectMenu, GeneralUpload } from 'src/components/general';
import { FormProvider, RHFSwitch, RHFTextField } from 'src/components/hook-form';
import { SkeletonForm } from 'src/components/skeleton';
import { APP_DUAL_LIST, APP_MEDIA_REWARDS_LIST } from 'src/constants/queryKeys';
import useFormSubmissionHandler from 'src/hooks/useFormSubmissionHander';
import { networkCreateDuel, networkUpdateDuel, useFetchDuelGroup } from 'src/network/duels';
import { ContentSelectionTableModal } from 'src/sections/@dashboard/project/feedItems';
import useAppInfoStore from 'src/store/appInfo';
import useFormsModalStore from 'src/store/forms';
import { generateSelectMenuItems } from 'src/utils';
import * as Yup from 'yup';
import { getDefaultValues } from './defaultValues';
import { mediaRewardsSchema, SCHEMA1, SCHEMA2, SCHEMA3, SCHEMA4 } from './validationSchema';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { networkGetMediaRewardsInsert, networkGetMediaRewardsUpdate } from 'src/network/mediaRewars';
import { useQueryClient } from '@tanstack/react-query';

export default function MediaRewardsForm() {
  const { modal, closeModal } = useFormsModalStore();
  const queryClient = useQueryClient();

  const { appInfo } = useAppInfoStore();

  const { projectId } = useParams();

  const duelDetails = modal?.data;

  const MediaRewardsID = modal?.MediaRewardsID;

  const [isLoading, setIsLoading] = useState(false);
  const { setModal, modal: modalData } = useFormsModalStore();
  const data = modalData?.data;

  const isEdit = modalData?.isEdit;

  const [selectedAmountType, setSelectedAmountType] = useState('');

  const [popupTypes, setPopupTypes] = useState([]);
  const [selectedPopupType, setSelectedPopupType] = useState('');

  const { enqueueSnackbar } = useSnackbar();

  const [amountTypes, setAmountTypes] = useState([]);

  const defaultValues = getDefaultValues(data);

  const methods = useForm({
    resolver: yupResolver(mediaRewardsSchema),
    defaultValues,
  });

  const {
    reset,
    trigger,
    handleSubmit,
    formState: { isSubmitting },
    setValue,
    control,
  } = methods;

  const onSubmit = async (values, event) => {
    const payload = {
      ...values,
      MeidaRewardsTransactionAmountTypeID: selectedAmountType,
      MediaRewardsApplicationID: +projectId,
    };

    try {
      setIsLoading(true);

      if (isEdit) {
        const res = await networkGetMediaRewardsUpdate(MediaRewardsID, payload);
        if (res?.data?.items?.ResultID === 0) {
          enqueueSnackbar('مشکلی پیش آمده، لطفا مجددا امتحان کنید', { variant: 'error' });
        } else {
          setModal({ isOpen: false, data: null, type: APP_MEDIA_REWARDS_LIST, title: 'ایجاد پاداش' });
          await queryClient.invalidateQueries({ queryKey: [APP_MEDIA_REWARDS_LIST] });
          enqueueSnackbar('وضعیت نظر موردنظر بروزرسانی شد');
        }
      } else {
        const res = await networkGetMediaRewardsInsert(payload);
        if (res?.data?.items?.ResultID === -3004) {
          enqueueSnackbar(res?.data?.items?.Desc, { variant: 'error' });
        } else if (res?.data?.items?.ResultID === -1) {
          enqueueSnackbar('ثبت اطلاعات با مشکل مواجه شده است', { variant: 'error' });
        } else {
          setModal({ isOpen: false, data: null, type: APP_MEDIA_REWARDS_LIST, title: 'ایجاد پاداش' });
          await queryClient.invalidateQueries({ queryKey: [APP_MEDIA_REWARDS_LIST] });
          enqueueSnackbar('پاداش موردنظر با موفقیت اضافه شد');
        }
      }

      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const popupTypeList = generateSelectMenuItems(appInfo?.DuelPopupType, 'Title', 'Value');
    const amountTypeList = generateSelectMenuItems(appInfo?.AmountType, 'TransactionAmountTypeLable', 'TransactionAmountTypeID');

    setPopupTypes(popupTypeList);
    setSelectedPopupType(duelDetails?.DuelPopupTypeID || popupTypeList[0]?.id);

    setAmountTypes(amountTypeList);
    setSelectedAmountType(duelDetails?.TransactionAmountTypeID || amountTypeList[0]?.id);
  }, []);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField name="MediaRewardsTimeCount" type="number" label="زمان دیدن ویدیو(ثانیه)" />

              <GeneralSelectMenu
                label="نوع پاداش"
                name="MeidaRewardsTransactionAmountTypeID"
                options={amountTypes}
                value={selectedAmountType}
                onChangeValue={setSelectedAmountType}
              />
              <RHFTextField name="MeidaRewardsValue" type="number" label="مقدار پاداش" />
              <RHFTextField name="MediaRewardsScore" type="number" label="امتیاز پاداش" />
              <RHFTextField name="MediaRewardsClubPoint" type="number" label="امتیاز باشگاه" />
              <RHFTextField name="MediaRewardsUserLevel" type="number" label="سطح کاربر" />
            </Box>

            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                mt: 3,
                gridTemplateColumns: {
                  xs: 'repeat(1, 1fr)',
                  sm: `repeat(${selectedPopupType === 1 ? 1 : 2}, 1fr)`,
                },
              }}
            ></Box>

            <div className="flex items-center gap-4 mt-6">
              <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                ذخیره
              </LoadingButton>

              <Button onClick={closeModal} variant="contained" color="error">
                انصراف
              </Button>
            </div>
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
